/**
 * Session Manager
 * Uses skin_id for feature tracking. Creates SkinAnalysis via POST /api/hair/session.
 */

import { hairAppPath as HairAppPath, HAIR_DETECTION_SESSION_KEY as Hair_DETECTION_SESSION_KEY } from '../config/hairApp';
import { Hair_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';

export const SESSION_STORAGE_KEY = Hair_DETECTION_SESSION_KEY;
const URL_PARAM_SESSION = 'session_id';

/**
 * Get session_id from URL query (?session_id=xxx or ?sessionId=xxx).
 * @param {{ search?: string } | string} locationOrSearch - useLocation() or location.search
 * @returns {string|null}
 */
export const getSessionIdFromUrl = (locationOrSearch) => {
  const search = typeof locationOrSearch === 'string' ? locationOrSearch : (locationOrSearch?.search || '');
  if (!search) return null;
  const params = new URLSearchParams(search);
  const sid = params.get(URL_PARAM_SESSION) || params.get('sessionId');
  return sid ? String(sid).trim() || null : null;
};

/**
 * Append session_id to path for navigation so it persists in URL (avoids loss when state is cleared).
 * @param {string} path - e.g. '/user-info', '/capture'
 * @param {string|null} sessionId
 * @returns {string} path with ?session_id= or &session_id=
 */
export const pathWithSessionId = (path, sessionId) => {
  const prefixed = HairAppPath(path);
  if (!sessionId || !String(sessionId).trim()) return prefixed;
  const base = prefixed.split('?')[0];
  const existing = prefixed.includes('?') ? prefixed.slice(prefixed.indexOf('?')) : '';
  const params = new URLSearchParams(existing);
  params.set(URL_PARAM_SESSION, String(sessionId).trim());
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/**
 * Get stored session/skin ID (string - may be skin_id as "123" or legacy UUID)
 * @returns {string|null}
 */
export const getSessionId = () => {
  return localStorage.getItem(SESSION_STORAGE_KEY);
};

/**
 * Get skin_id (number) if we have a numeric session_id
 * @returns {number|null}
 */
export const getSkinId = () => {
  const sid = getSessionId();
  if (!sid || typeof sid !== 'string') return null;
  const n = parseInt(sid, 10);
  return Number.isNaN(n) ? null : n;
};

/**
 * Create a new session (resets current) - calls backend to create SkinAnalysis
 * @param {string} [apiBaseUrl] - If provided, creates via backend; otherwise local only
 * @returns {Promise<string>} session_id (str(skin_id) or UUID)
 */
export const createNewSession = async (apiBaseUrl) => {
  if (apiBaseUrl) {
    return createNewSessionWithBackend(apiBaseUrl);
  }
  const id = createNewSessionLocal();
  return id;
};

/**
 * Create new session without API (local only, for fallback)
 * @returns {string}
 */
export const createNewSessionLocal = () => {
  const newSessionId = generateUUID();
  localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
  return newSessionId;
};

/**
 * Clear current session
 */
export const clearSession = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const timestamp = Date.now().toString(36);
  const randomPart = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  const performanceId =
    typeof performance !== 'undefined' && performance.now
      ? Math.floor(performance.now() * 1000).toString(36)
      : '';
  return `${timestamp}-${performanceId}-${randomPart}`;
}

/**
 * Initialize session with backend.
 * Creates new SkinAnalysis (POST) when no session, or validates existing (GET).
 * When app_session_token is provided, new sessions are created with it so the flow stays tied to the same token.
 * @param {string} apiBaseUrl
 * @param {{ app_session_token?: string }} [options] - optional; pass token so new session stays in same token flow
 * @returns {Promise<string>} session_id (skin_id as string for new flow)
 */
export const initializeSession = async (apiBaseUrl, options = {}) => {
  try {
    const stored = getSessionId();
    const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');

    if (!stored) {
      return createNewSessionWithBackend(baseUrl, options);
    }

    const isNumeric = /^\d+$/.test(String(stored).trim());
    let url = isNumeric
      ? `${baseUrl}${Hair_API_PREFIX}/session?skin_id=${stored}`
      : `${baseUrl}${Hair_API_PREFIX}/session?session_id=${encodeURIComponent(stored)}`;

    if (options.app_session_token) {
      url += `&app_session_token=${encodeURIComponent(options.app_session_token)}`;
    }

    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      timeoutMs: 20000,
    });

    if (response.ok) {
      const data = await response.json();
      const sid = data.skin_id != null ? String(data.skin_id) : data.session_id;
      if (sid && sid !== stored) {
        localStorage.setItem(SESSION_STORAGE_KEY, sid);
        return sid;
      }
      return stored;
    }
    return createNewSessionWithBackend(baseUrl, options);
  } catch (error) {
    // When token is present, prefer existing stored session over generating a random one
    if (options.app_session_token && getSessionId()) return getSessionId();
    return getSessionId() || createNewSessionLocal();
  }
};

/**
 * Create a new skin analysis session via backend.
 * Optionally pass app_session_token (e.g. from landing page URL) and device_info to store at CTA time.
 * @param {string} apiBaseUrl
 * @param {{ app_session_token?: string, device_info?: object, previous_Hair_reference?: string }} [options] - optional; previous_Hair_reference migrates existing customer rows to the new session id
 * @returns {Promise<string>} session_id (str(skin_id))
 */
export const createNewSessionWithBackend = async (apiBaseUrl, options = {}) => {
  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  const body = {};
  if (options.app_session_token) {
    body.app_session_token = options.app_session_token;
  }
  if (options.device_info && typeof options.device_info === 'object') {
    body.device_info = options.device_info;
  }
  const prevRef = options.previous_Hair_reference;
  if (prevRef && String(prevRef).trim()) {
    body.previous_Hair_reference = String(prevRef).trim();
  }

  let response;
  try {
    response = await fetchWithTimeout(`${baseUrl}${Hair_API_PREFIX}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      timeoutMs: 20000,
    });
  } catch {
    if (options.app_session_token && getSessionId()) return getSessionId();
    return createNewSessionLocal();
  }

  if (!response.ok) {
    let msg = `Session ${response.status}`;
    try {
      const errData = await response.json();
      if (errData.error) msg = errData.error;
    } catch (_) {}
    throw new Error(msg);
  }

  const data = await response.json();
  const sid = data.skin_id != null ? String(data.skin_id) : data.session_id;
  if (!sid) {
    throw new Error('No session id in response');
  }
  localStorage.setItem(SESSION_STORAGE_KEY, sid);
  return sid;
};
